import type { Lead } from '../types';

export const STATUS_SORT_ORDER: Record<string, number> = {
  'Lead': 1,
  'Quoted': 2,
  'Approved': 3,
  'Awarded': 3,
  'Accepted': 3,
  'Finalizing': 4,
  'Onboarding': 4,
  'Complete': 5,
  'Transferred': 6,
  'Declined': 7,
  'Not Awarded': 8,
};

/**
 * Returns the urgency alert rank of a lead:
 * 0: 🟡 Yellow Alert (Due Today)
 * 1: 🔴 Red Alert (Overdue >= 7 days)
 * 2: No active alert
 */
export function getLeadAlertRank(lead: Lead, referenceDate = new Date()): number {
  if (!lead.followUpDate || lead.followUpCompletedAt) return 2;
  const todayStr = referenceDate.toISOString().slice(0, 10);
  if (lead.followUpDate === todayStr) {
    return 0; // Yellow Alert (Due Today)
  }
  const target = new Date(lead.followUpDate);
  if (!isNaN(target.getTime())) {
    const diffMs = referenceDate.getTime() - target.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays >= 7) {
      return 1; // Red Alert (Overdue >= 7 days)
    }
  }
  return 2;
}

/**
 * Calculates default view priority tier:
 * Tier 0: Opportunity Lead with Yellow Alert (Due Today)
 * Tier 1: Opportunity Lead with Red Alert (Overdue >= 7d)
 * Tier 2: Opportunity Lead without alert (top of Status column in Opportunity)
 * Tier 3: Other status/pipeline with Yellow Alert (Due Today)
 * Tier 4: Other status/pipeline with Red Alert (Overdue >= 7d)
 * Tier 5: Other records without alert
 */
export function getDefaultSortPriorityTier(lead: Lead, referenceDate = new Date()): number {
  const isOpp = lead.pipeline === 'Opportunity' || !lead.pipeline;
  const isLeadStatus = lead.status === 'Lead';
  const alertRank = getLeadAlertRank(lead, referenceDate);

  if (isOpp) {
    if (isLeadStatus) {
      if (alertRank === 0) return 0;
      if (alertRank === 1) return 1;
      return 2;
    }
    if (alertRank === 0) return 3;
    if (alertRank === 1) return 4;
    return 5;
  }

  // Non-Opportunity pipelines:
  if (alertRank === 0) return 3;
  if (alertRank === 1) return 4;
  return 5;
}

/**
 * Sorts leads using the Default View rules:
 * 1. Opportunity >> Lead Status at the top of the Status Column
 * 2. Yellow Alert Due Today at the very top of Default View
 * 3. Most recent Date Entered (inquiryDate) / creation date descending
 */
export function sortLeadsDefault(leads: Lead[], referenceDate = new Date()): Lead[] {
  return [...leads].sort((a, b) => {
    const tierA = getDefaultSortPriorityTier(a, referenceDate);
    const tierB = getDefaultSortPriorityTier(b, referenceDate);
    if (tierA !== tierB) {
      return tierA - tierB;
    }

    // Secondary tie-breaker: Newest Date Entered (inquiryDate) / created / contacted descending
    const dateA = a.inquiryDate || a.createdAt || a.lastContacted || '';
    const dateB = b.inquiryDate || b.createdAt || b.lastContacted || '';
    if (dateA !== dateB) {
      return dateB.localeCompare(dateA);
    }

    return (a.company || a.name || '').localeCompare(b.company || b.name || '');
  });
}

/**
 * Sorts leads by a specific column or field, respecting status ordering and alert ranking.
 */
export function sortLeadsByField(
  leads: Lead[],
  sortField: string,
  sortDir: 'asc' | 'desc',
  getLeadLastUpdateDate?: (lead: Lead) => Date | null,
  referenceDate = new Date()
): Lead[] {
  const dir = sortDir === 'asc' ? 1 : -1;
  return [...leads].sort((a, b) => {
    if (sortField === 'status') {
      const orderA = STATUS_SORT_ORDER[a.status] || 99;
      const orderB = STATUS_SORT_ORDER[b.status] || 99;
      if (orderA !== orderB) {
        return (orderA - orderB) * dir;
      }
      // If status matches, Yellow Alert Due Today first
      const alertA = getLeadAlertRank(a, referenceDate);
      const alertB = getLeadAlertRank(b, referenceDate);
      if (alertA !== alertB) {
        return alertA - alertB;
      }
    }

    if (sortField === 'dealValue' || sortField === 'estimatedDealValue') {
      return (((a.estimatedDealValue || a.dealValue) || 0) - ((b.estimatedDealValue || b.dealValue) || 0)) * dir;
    }

    if (sortField === 'lastUpdateDate' || sortField === 'lastContacted') {
      const ad = getLeadLastUpdateDate ? (getLeadLastUpdateDate(a)?.getTime() || 0) : 0;
      const bd = getLeadLastUpdateDate ? (getLeadLastUpdateDate(b)?.getTime() || 0) : 0;
      return (ad - bd) * dir;
    }

    if (sortField === 'followUpDate') {
      const fa = a.followUpDate || '9999-99-99';
      const fb = b.followUpDate || '9999-99-99';
      return fa.localeCompare(fb) * dir;
    }

    const valA = (sortField === 'lead' || sortField === 'leadName')
      ? (a.company || a.name || '')
      : String((a as any)[sortField] || '');
    const valB = (sortField === 'lead' || sortField === 'leadName')
      ? (b.company || b.name || '')
      : String((b as any)[sortField] || '');
    const av = valA.toLowerCase();
    const bv = valB.toLowerCase();
    if (!av && bv) return 1;
    if (av && !bv) return -1;
    return av.localeCompare(bv) * dir;
  });
}
